import { AfterContentInit, Component, ContentChildren, ElementRef, Host, Input, OnDestroy, Optional, QueryList, Self } from '@angular/core'
import { FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatOption } from '@angular/material/core'
import { MatFormFieldControl } from '@angular/material/form-field'
import { MatSelect } from '@angular/material/select'
import { FocusMonitor } from '@angular/cdk/a11y'
import { filter, startWith, Subject, takeUntil, tap } from 'rxjs'

import { CustomMaterialInputControlComponent } from '../custom-field'

@Component({
  selector: 'mmx-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SearchableSelectComponent,
    },
  ],
  standalone: false,
})
export class SearchableSelectComponent
  extends CustomMaterialInputControlComponent<string>
  implements OnDestroy, AfterContentInit
{
  @Input() isSelectAll = false
  @ContentChildren(MatOption, { descendants: true })
  public queryOptions: QueryList<MatOption>
  public searchControl = new FormControl<string | null>(null)
  public searching = false
  public filteredValues: any[] = []
  private onDestroy$ = new Subject<void>()
  public filteredOptions$ = this.searchControl.valueChanges.pipe(
    takeUntil(this.onDestroy$),
    startWith(''),
    filter(() => this.queryOptions?.length > 0),
    tap((search) => {
      this.applyFilter(search)
    }),
  ).subscribe()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected _parentForm: NgForm,
    @Optional() protected _parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    @Optional() @Host() public matSelect: MatSelect,
  ) {
    super(ngControl, _parentForm, _parentFormGroup, fm, elRef)
  }

  ngAfterContentInit(): void {
    this.initFilteredValues()
    this.queryOptions.changes.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(() => {
      this.initFilteredValues()
    })
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private initFilteredValues(): void {
    if (!this.searchControl.value) {
      this.filteredValues = this.queryOptions.map((option) => option.value)
    }
  }

  private applyFilter(search: string | null): void {
    const filtered: any[] = []
    const normalizedSearch = search?.toLowerCase() || ''
    for (const option of this.queryOptions) {
      const $el = option._getHostElement()
      const show = !normalizedSearch || option.viewValue.toLowerCase().includes(normalizedSearch)
      $el.classList.toggle('hidden', !show)
      if (show) {
        filtered.push(option.value)
      }
    }
    this.filteredValues = filtered
  }
}
